import React, { useEffect } from 'react';
import { required } from 'redux-freeform';
import { FormInputColumn, FormSelect, Spinner } from '@thecb/components';
import {
  reducer,
  mapStateToProps,
  mapDispatchToProps,
} from './TerminalLoginForm.state';
import { useDispatch, useSelector } from 'react-redux';
import { clearSharedAlerts, replaceSharedAlerts } from '../../state/shared';
import SharedAlertBar from '../../components/alert-bar/SharedAlertBar';
import { fetchTerminals } from './TerminalLogin.state';

const defaultLocationOption = { text: 'Select Location', value: '' };
const mapLocationsToSelectOptions = (locations) => {
  const options = [defaultLocationOption];
  return options.concat(
    locations.data.map((location) => ({
      text: location.name,
      value: location.id.toString(),
    })),
  );
};
const defaultTerminalOption = { text: 'Select Terminal', value: '' };
const mapTerminalsToSelectOptions = (terminals, locationId) => {
  const options = [defaultTerminalOption];
  return options.concat(
    terminals.data
      .filter((t) => t.location_id == locationId)
      .map((terminal) => ({
        text: terminal.name,
        value: terminal.id.toString(),
      })),
  );
};

export const TerminalLoginForm = ({
  fields,
  actions,
  showErrors,
  locations,
  terminals,
  setSelectedTerminalSession,
}) => {
  const dispatch = useDispatch();
  const { alerts: sharedAlerts } = useSelector((state) => state.shared);

  const isAlertCurrentlyDisplayed = !!sharedAlerts.length;

  const handleLocationSelection = (locationId) => {
    actions.fields.location.set(locationId);
    if (locationId) {
      dispatch(fetchTerminals(locationId));
    }
    if (isAlertCurrentlyDisplayed) {
      dispatch(clearSharedAlerts());
    }
  };

  const handleTerminalSelection = (terminalId) => {
    actions.fields.terminal.set(terminalId);
    const selectedTerminalSession = terminals.data.find(
      (t) => t.id === Number(terminalId),
    );
    const hasActiveSession =
      selectedTerminalSession?.terminal_session.cashier['active?'];
    hasActiveSession &&
      dispatch(
        replaceSharedAlerts({
          heading: 'Open Session Already Exists',
          text: 'This terminal currently has an open session.',
          type: 'info',
        }),
      );
    !hasActiveSession &&
      isAlertCurrentlyDisplayed &&
      dispatch(clearSharedAlerts());
    setSelectedTerminalSession(selectedTerminalSession);
  };

  useEffect(() => {
    return () => actions.form.clear();
  }, []);
  useEffect(() => {
    // when the user re-selects a location, ensure
    // the terminals select is reset as well.
    actions.fields.terminal.set('');
  }, [fields.location.rawValue]);

  const currentLocationId = fields.location.rawValue;
  const locationErrors = {
    [required.error]: 'Location selection is required',
  };
  const terminalErrors = {
    [required.error]: 'Terminal selection is required',
  };
  return (
    <FormInputColumn role="form" aria-label="Login">
      {locations.fetching ? (
        <Spinner size="100" />
      ) : (
        <FormSelect
          onChange={handleLocationSelection}
          dropdownMaxHeight="200px"
          labelTextWhenNoError="Location"
          options={mapLocationsToSelectOptions(locations)}
          fieldActions={actions.fields.location}
          showErrors={showErrors}
          errorMessages={locationErrors}
          field={fields.location}
          labelTextVariant="p"
          errorLabelTextVariant="p"
        />
      )}
      {terminals.fetching ? (
        <Spinner size="100" />
      ) : (
        fields.location.rawValue && (
          <FormSelect
            onChange={handleTerminalSelection}
            dropdownMaxHeight="200px"
            labelTextWhenNoError="Terminal"
            options={mapTerminalsToSelectOptions(terminals, currentLocationId)}
            fieldActions={actions.fields.terminal}
            showErrors={showErrors}
            errorMessages={terminalErrors}
            field={fields.terminal}
            labelTextVariant="p"
            errorLabelTextVariant="p"
          />
        )
      )}
      {sharedAlerts.length > 0 && <SharedAlertBar showQuitLink={false} />}
    </FormInputColumn>
  );
};

TerminalLoginForm.reducer = reducer;
TerminalLoginForm.mapStateToProps = mapStateToProps;
TerminalLoginForm.mapDispatchToProps = mapDispatchToProps;

export default TerminalLoginForm;
